<template>
  <div>
    <h2 class="shipment-header">{{ $t('trackingPage.header') }}</h2>

    <div v-if="possibleDelay && isStatusDelivered === false" class="delay-banner">
      {{ $t('trackingPage.delayBanner') }}
    </div>

    <section
      class="shipment-eta"
      v-if="!!trackingData.thirdPartyCourier && !!trackingData.thirdPartyTrackingUrl"
    >
      <div class="shipment-eta-container">
        <h2>
          {{ $t('trackingPage.thirdPartyCourierPre') }}
          <span class="courier-name">{{ trackingData.thirdPartyCourier.toUpperCase() }}</span
          >, {{ $t('trackingPage.thirdPartyCourierPost') }}
          <a
            :href="trackingData.thirdPartyTrackingUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="tracking-link"
          >
            {{ $t('trackingPage.here') }}
          </a>
        </h2>
      </div>
    </section>

    <section
      class="shipment-eta"
      v-if="
        isStatusDelivered === false &&
          isLost === false &&
          isCompleteCancelled === false &&
          !trackingData.thirdPartyCourier &&
          !trackingData.thirdPartyTrackingUrl
      "
    >
      <div class="shipment-eta-container">
        <h2 class>{{ $t('trackingPage.estimatedDelivery') }}</h2>
        <p class="delivery-day">
          <span v-if="trackingData.eta">
            {{ formatDateTime(trackingData.eta['startTime'], trackingData.eta['endTime']) }}
          </span>
          <span v-else-if="trackingData.expectedDayOfDelivery">
            {{ formatExpectedDate(trackingData.expectedDayOfDelivery) }}
          </span>
          <!-- No estimate (e.g. parcel in transit to Cyprus): fall back to the delivery promise
               while it is still ahead, otherwise say a date is pending rather than leave a blank. -->
          <span v-else-if="isPromiseStillAhead">
            {{
              $t('trackingPage.untilPromiseDate', {
                date: formatExpectedDate(trackingData.deliveryPromiseTo),
              })
            }}
          </span>
          <span v-else>{{ $t('trackingPage.deliveryDatePending') }}</span>
        </p>
      </div>
      <br />
      <div class="shipment-eta-container">
        <p class="order-status">
          {{
            changeDeliveryFailText(
              trackingData.failedDeliveryText,
              trackingData.failedDeliveryTextEn,
            ).text
          }}
          <a
            v-if="
              changeDeliveryFailText(
                trackingData.failedDeliveryText,
                trackingData.failedDeliveryTextEn,
              ).subText
            "
            href="#"
            @click.prevent="openPhoneModal"
            class="click-here"
          >
            {{
              changeDeliveryFailText(
                trackingData.failedDeliveryText,
                trackingData.failedDeliveryTextEn,
              ).subText
            }}
          </a>
        </p>
      </div>
    </section>

    <section class="shipment-state">
      <div class="status-container">
        <h2>
          {{ $i18n.locale === 'el' ? trackingData.statusTextGr : trackingData.statusTextEn }}
        </h2>
        <div v-if="isStatusDelivered && lockerReturnEnabled" class="return-button-container">
          <button @click="handleReturnClick">
            {{ $t('trackingPage.returnButton') }}
          </button>
          <p class="return-info">{{ $t('trackingPage.returnInfo') }}</p>
        </div>
      </div>

      <progress
        :value="isStatusDelivered || isCompleteCancelled ? 4 : progressBar"
        max="4"
      ></progress>

      <div class="grid-table">
        <p>
          <span class="title-col">{{ $t('trackingPage.shipmentId') }}</span>
          <span>{{ shipmentTrackingId }}</span>
        </p>
        <p v-if="userOrderCode">
          <span class="title-col">{{ $t('trackingPage.orderId') }}</span>
          <a
            :href="'https://www.skroutz.gr/account/ecommerce/orders/' + userOrderCode"
            target="_blank"
            rel="nofollow"
            >{{ userOrderCode }}</a
          >
        </p>
      </div>
    </section>

    <section>
      <ul v-if="convertedTrackingDetails.length > 0" class="tracking-steps">
        <li
          v-for="detail in convertedTrackingDetails"
          :key="detail.id"
          :class="!detail.isNotDelivered ? 'delivered-step' : ''"
        >
          <article>
            <p>{{ $i18n.locale === 'el' ? detail.description_gr : detail.description_en }}</p>
            <p>{{ detail.createdAt }}</p>
          </article>
        </li>
      </ul>

      <ul v-else class="tracking-steps">
        <li>
          <article>
            <p>{{ $t('trackingPage.routeCreated') }}</p>
          </article>
        </li>
      </ul>
    </section>
    <div v-if="isStatusDelivered === true && hasRightToDeclarePickupAlert === true">
      <section
        class="reservation-section"
        v-if="!trackingData.thirdPartyCourier && !trackingData.thirdPartyTrackingUrl"
      >
        <button class="reservation-button" @click="declarePickupAlert">
          {{ $t('trackingPage.reservationButton') }}
        </button>
      </section>
    </div>
    <div v-if="hasAlreadyDeclaredPickupAlert === true">
      <section class="reservation-section">
        <h2>{{ $t('trackingPage.reservationSuccess') }}</h2>
      </section>
    </div>
    <div v-if="showPhoneModal" class="phone-modal-overlay">
      <div class="phone-modal">
        <h2>{{ $t('trackingPage.phoneModalTitle') }}</h2>

        <div class="phone-input-group">
          <select v-model="selectedPrefix" class="prefix-dropdown">
            <option v-for="country in countryPrefixes" :key="country.code" :value="country.prefix">
              {{ country.code }} ({{ country.prefix }})
            </option>
          </select>
          <input
            type="text"
            v-model="phoneNumber"
            @input="validatePhoneInput"
            :placeholder="$t('trackingPage.phonePlaceholder')"
            class="phone-input"
          />
        </div>

        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <div class="modal-buttons">
          <button @click="submitPhoneNumber" class="submit-btn">
            {{ $t('trackingPage.submitButton') }}
          </button>
          <button @click="closePhoneModal" class="close-btn">
            {{ $t('trackingPage.cancelButton') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Bugsnag from '@bugsnag/js';
import axios from 'axios';

export default {
  name: 'MainPage',
  props: {
    trackingData: {
      type: Object,
      required: true,
    },
    shipmentTrackingId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      convertedTrackingDetails: [],
      userOrderCode: '',
      isStatusDelivered:
        this.trackingData.status === 'delivered' ||
        (this.trackingData.status === 'at_locker' && this.trackingData.isReturn === false) ||
        (this.trackingData.status === 'at_locker' &&
          this.trackingData.isReturn === true &&
          (this.trackingData.internalState === 'matched' ||
            this.trackingData.internalState === 'not_routed'))
          ? true
          : false,
      isLost: this.trackingData.status === 'lost_or_damaged' ? true : false,
      progressBar: '0.1',
      isPickedUp: false,
      isReturn: this.trackingData.isReturn,
      failedDeliveryText: this.trackingData.failedDeliveryText
        ? this.trackingData.failedDeliveryText
        : '',
      isCompleteCancelled: this.trackingData.isCompleteCancelled,
      lockerReturnEnabled: this.trackingData.lockerReturnEnabled,
      possibleDelay: this.trackingData.possibleDelay,
      hasRightToDeclarePickupAlert: this.trackingData.hasRightToDeclarePickupAlert,
      hasAlreadyDeclaredPickupAlert: this.trackingData.hasAlreadyDeclaredPickupAlert,
      showPhoneModal: false,
      selectedPrefix: '+30', // Default to Greece
      phoneNumber: '',
      errorMessage: '',
      countryPrefixes: [
        { code: 'GR', prefix: '+30' },
        { code: 'CY', prefix: '+357' },
        { code: 'AL', prefix: '+355' },
        { code: 'BG', prefix: '+359' },
        { code: 'GE', prefix: '+995' },
        { code: 'IT', prefix: '+39' },
        { code: 'RU', prefix: '+7' },
        { code: 'TR', prefix: '+90' },
        { code: 'GB', prefix: '+44' },
        { code: 'DE', prefix: '+49' },
        { code: 'FR', prefix: '+33' },
        { code: 'ES', prefix: '+34' },
        { code: 'US', prefix: '+1' },
        { code: 'PT', prefix: '+351' },
        { code: 'NL', prefix: '+31' },
        { code: 'BE', prefix: '+32' },
        { code: 'GI', prefix: '+350' },
        { code: 'LU', prefix: '+352' },
        { code: 'IE', prefix: '+353' },
        { code: 'IS', prefix: '+354' },
        { code: 'MT', prefix: '+356' },
        { code: 'FI', prefix: '+358' },
        { code: 'HU', prefix: '+36' },
        { code: 'LT', prefix: '+370' },
        { code: 'LV', prefix: '+371' },
        { code: 'EE', prefix: '+372' },
        { code: 'MD', prefix: '+373' },
        { code: 'AM', prefix: '+374' },
        { code: 'BY', prefix: '+375' },
        { code: 'AD', prefix: '+376' },
        { code: 'MC', prefix: '+377' },
        { code: 'SM', prefix: '+378' },
        { code: 'VA', prefix: '+379' },
        { code: 'UA', prefix: '+380' },
        { code: 'RS', prefix: '+381' },
        { code: 'ME', prefix: '+382' },
        { code: 'XK', prefix: '+383' },
        { code: 'HR', prefix: '+385' },
        { code: 'SI', prefix: '+386' },
        { code: 'BA', prefix: '+387' },
        { code: 'MK', prefix: '+389' },
        { code: 'RO', prefix: '+40' },
        { code: 'CH', prefix: '+41' },
        { code: 'CZ', prefix: '+420' },
        { code: 'SK', prefix: '+421' },
        { code: 'LI', prefix: '+423' },
        { code: 'AT', prefix: '+43' },
        { code: 'DK', prefix: '+45' },
        { code: 'SE', prefix: '+46' },
        { code: 'NO', prefix: '+47' },
        { code: 'PL', prefix: '+48' },
        { code: 'AZ', prefix: '+994' },
      ],
    };
  },
  computed: {
    // The promise is set at order creation from the pickup date, so for a parcel that has been
    // in transit for days it is already past; only show it while it is today or later.
    isPromiseStillAhead: function() {
      const promise = this.trackingData.deliveryPromiseTo;
      if (!promise) return false;
      const promiseDay = new Date(promise);
      const today = new Date();
      promiseDay.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return !isNaN(promiseDay) && promiseDay >= today;
    },
    adjustedTrackingDetails: function() {
      return this.trackingData['trackingDetails'].map((el, index) => {
        // el.description = this.mapTracking(
        //   el.description,
        //   this.isPickedUp,
        //   this.isReturn,
        //   el.notes
        // ); // check when is it false
        el.createdAt = this.formatDateTime(el.createdAt);

        // setting bullet class background if delivered
        if (
          index === this.trackingData['trackingDetails'].length - 1 &&
          el.latestEta &&
          this.isDateDelivered(el.latestEta)
        ) {
          el.isNotDelivered = true;
        }

        return el;
      });
    },
  },
  mounted() {
    this.convertedTrackingDetails = this.adjustedTrackingDetails.filter(
      t => t.description_gr.length > 0 || t.description_en.length > 0,
    );
    this.userOrderCode = this.trackingData.userOrderCode ?? null;
    if (this.trackingData.countryCode === 'CY' && process.env.VUE_APP_COUNTRY_CODE === 'GR') {
      window.location.href = `${process.env.VUE_APP_BASE_URL_CY}/#${this.shipmentTrackingId}`;
    }

    if (this.trackingData.needsFallbackAddress) {
      if (window.drift && window.drift.api) {
        window.drift.api.startInteraction({
          interactionId: 314234,
          replaceActiveConversation: false,
        });
      } else {
        window.drift.on('ready', () => {
          window.drift.api.startInteraction({
            interactionId: 314234,
            replaceActiveConversation: false,
          });
        });
      }
      return;
    }

    if (
      this.trackingData.status === 'attempted_delivery' &&
      this.trackingData.internalState === 'failed_delivery'
    ) {
      let curDate = new Date();
      let dayOfWeek = curDate.getDay();
      var isSunday = dayOfWeek === 0; // 6 = Saturday, 0 = Sunday
      //Weekdays playbook
      if (!isSunday) {
        if (window.drift && window.drift.api) {
          window.drift.api.startInteraction({
            interactionId: 294526,
            replaceActiveConversation: false,
          });
        } else {
          window.drift.on('ready', () => {
            window.drift.api.startInteraction({
              interactionId: 294526,
              replaceActiveConversation: false,
            });
          });
        }
        //Sunday playbook
      } else if (isSunday) {
        if (window.drift && window.drift.api) {
          window.drift.api.startInteraction({
            interactionId: 296848,
            replaceActiveConversation: false,
          });
        } else {
          window.drift.on('ready', () => {
            window.drift.api.startInteraction({
              interactionId: 296848,
              replaceActiveConversation: false,
            });
          });
        }
      }
    }
  },
  methods: {
    previewAfterCheck(responseDate, initialDate) {
      if (responseDate === 'Invalid Date') {
        Bugsnag.notify(
          `Track Id: ${this.shipmentTrackingId} - Error date fortmat catched: ${initialDate}`,
        );
        return '';
      }

      return responseDate;
    },
    formatDateTime(dateToDate, dateToTime = null) {
      if (!dateToDate) return '-';

      let formattedDate = new Date(dateToDate).toLocaleDateString(
        this.$i18n.locale === 'el' ? 'el-GR' : 'en-GB',
        {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23',
        },
      );
      formattedDate = this.previewAfterCheck(formattedDate, dateToDate);

      if (!dateToTime) return formattedDate;

      let formattedHour = new Date(dateToTime).toLocaleTimeString(
        this.$i18n.locale === 'el' ? 'el-GR' : 'en-GB',
        {
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23',
        },
      );
      formattedHour = this.previewAfterCheck(formattedHour, dateToTime);

      return `${formattedDate} - ${formattedHour}`;
    },
    formatExpectedDate(expectedDate) {
      let formattedDate = new Date(expectedDate).toLocaleDateString(
        this.$i18n.locale === 'el' ? 'el-GR' : 'en-GB',
        {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        },
      );
      return formattedDate;
    },
    isDateDelivered(dateEta) {
      // compare last step's time from current time
      return Date.parse(new Date(dateEta)) > Date.now();
    },
    hasSlot(trackingDetails) {
      let hasSlot = false;
      for (let detail of trackingDetails) {
        if (detail.description === 'Shipment arrived at locker') {
          hasSlot = true;
        }
      }
      return hasSlot;
    },
    changeDeliveryFailText(failedDeliveryText, failedDeliveryTextEn) {
      if (!failedDeliveryText) return { text: '', subText: '' };
      let text = failedDeliveryText;
      let subText = '';
      if (
        failedDeliveryText ===
        'Επίλεξε "Χρειάζεσαι βοήθεια;" > "Επαναδρομολόγηση μετά από αποτυχία παράδοσης", στο τέλος της σελίδας, ώστε να καταχωρήσεις νέα ημερομηνία ή και διεύθυνση παράδοσης που σε εξυπηρετεί.'
      ) {
        text = this.$t('trackingPage.failText1');
        subText = this.$t('trackingPage.here');
      } else if (
        failedDeliveryText ===
        'Η επιστροφή του δέματος στη θυρίδα δεν ολοκληρώθηκε. Θα χρειαστούμε μια φυσική διεύθυνση για να την παραλάβουμε από το χώρο σου. Ακολούθησε το σύνδεσμο “Χρειάζεσαι βοήθεια” για να την καταχωρήσεις'
      ) {
        text = this.$t('trackingPage.failText2');
        subText = this.$t('trackingPage.here');
      } else if (
        failedDeliveryText ===
        'Δεν καταφέραμε να παραδώσουμε το δέμα σου στο Skroutz Point. Θα γίνει νέα προσπάθεια παράδοσης και θα ενημερωθείς με γραπτό μήνυμα. Εναλλακτικά αν επιθυμείς παράδοση σε φυσική διεύθυνση μπορείς να κάνεις αίτημα επαναδρομολόγησης στο σύνδεσμο Χρειάζεσαι βοήθεια'
      ) {
        text = this.$t('trackingPage.failText3');
        subText = this.$t('trackingPage.here');
      } else if (
        failedDeliveryText ===
        'Η παραγγελία σου δεν είναι εφικτό να τοποθετηθεί σε θυρίδα. Θα χρειαστούμε μια φυσική διεύθυνση για την παράδοσή της. Ακολούθησε το σύνδεσμο “Χρειάζεσαι βοήθεια” για να την καταχωρήσεις'
      ) {
        text = this.$t('trackingPage.failText4');
        subText = this.$t('trackingPage.here');
      } else if (
        failedDeliveryText ===
        'Η παραγγελία σου δεν είναι εφικτό να τοποθετηθεί σε θυρίδα. Θα χρειαστούμε μια φυσική διεύθυνση για την παράδοση της. Ακολούθησε το σύνδεσμο “Χρειάζεσαι βοήθεια” για να την καταχωρήσεις'
      ) {
        text = this.$t('trackingPage.failText5');
        subText = this.$t('trackingPage.here');
      } else if (
        failedDeliveryText ===
        'Η παραγγελία σου δεν είναι εφικτό να τοποθετηθεί σε θυρίδα. Θα χρειαστούμε μια φυσική διεύθυνση για την παράδοση της. Μπορείς να ορίσεις νέα ημερομηνία παράδοσης και νέα διεύθυνση παράδοσης που να σε εξυπηρετεί.'
      ) {
        text = this.$t('trackingPage.failText6');
        subText = this.$t('trackingPage.here');
      } else if (
        failedDeliveryText ===
        'Επίλεξε "Χρειάζεσαι βοήθεια;" > "Επαναδρομολόγηση μετά από αποτυχία παράδοσης", στο τέλος της σελίδας, ώστε να καταχωρήσεις νέα ημερομηνία ή και διεύθυνση παράδοσης που σε εξυπηρετεί.'
      ) {
        text = this.$t('trackingPage.failText1');
        subText = this.$t('trackingPage.here');
      } else if (
        failedDeliveryText ===
        'Μπορείς να ορίσεις νέα ημερομηνία παράδοσης και νέα διεύθυνση παράδοσης που να σε εξυπηρετεί.'
      ) {
        text = this.$t('trackingPage.failText7');
        subText = this.$t('trackingPage.here');
      } else if (this.$i18n.locale !== 'el') {
        text = failedDeliveryTextEn || failedDeliveryText;
      }

      return { text, subText };
    },
    handleReturnClick() {
      const route = this.$router.resolve({ name: 'returnOrder' }).route;
      route.meta.navigationSource = 'return-button';
      this.$router.push({
        name: 'returnOrder',
        params: {
          dropoffType: '1',
          barcodeId: this.shipmentTrackingId,
        },
      });
    },
    async declarePickupAlert() {
      if (window.confirm(this.$t('trackingPage.confirmReservation'))) {
        await axios.post(
          `${process.env.VUE_APP_COURIER_API}/user/hp/declarePickupAlert/${this.shipmentTrackingId}`,
        );
        this.hasRightToDeclarePickupAlert = false;
        this.hasAlreadyDeclaredPickupAlert = true;
      }
    },
    openPhoneModal() {
      this.showPhoneModal = true;
      this.errorMessage = '';
      this.phoneNumber = '';
      this.selectedPrefix = '+30'; // Reset to default
    },

    closePhoneModal() {
      this.showPhoneModal = false;
    },

    validatePhoneInput() {
      // Remove any non-numeric characters
      this.phoneNumber = this.phoneNumber.replace(/\D/g, '');
    },

    async submitPhoneNumber() {
      if (!this.phoneNumber || this.phoneNumber.length < 6) {
        this.errorMessage = this.$t('trackingPage.errorInvalidPhone');
        return;
      }

      try {
        this.errorMessage = '';
        const response = await axios.post(
          `${process.env.VUE_APP_COURIER_API}/user/hp/validateUserPhoneNumber`,
          {
            shipmentId: this.shipmentTrackingId,
            mobilePhone: this.selectedPrefix + this.phoneNumber,
          },
        );
        const encodeVoucher = response.data.encodeVoucher;
        // If successful, close modal and redirect
        this.closePhoneModal();
        this.$router.push(`/reschedule/${encodeVoucher}`);
      } catch (error) {
        // Handle error
        this.errorMessage =
          error.response?.data?.error_description || this.$t('trackingPage.errorSubmit');
      }
    },
  },
};
</script>

<style>
.status-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 3px;
}

.return-button-container {
  width: 200px;
  position: relative;
}

.return-info {
  position: absolute;
  top: 100%;
}

button {
  flex-shrink: 0;
  display: block;
  padding: 10px 20px;
  border: 1px solid #e8760a;
  border-radius: 4px;
  background-color: #f68b24;
  font-size: 24px;
  color: #fff;
  text-align: center;
  width: 200px;
  &:hover {
    background-color: #e8760a;
  }
}

.shipment-header {
  font-family: 'Proxima Nova';
  font-weight: 500;
}
.grid-table p {
  display: grid;
  grid-template-columns: 120px auto;
}
.title-col {
  color: #707070 !important;
}
.tracking-steps li:last-child {
  min-height: unset;
}
.shipment-eta-container {
  display: flex;
}
.shipment-eta-container h2 {
  margin-top: auto;
  margin-bottom: auto;
}
.shipment-eta-container p {
  margin-top: auto;
  margin-bottom: auto;
  font-weight: bold;
}
.delivery-day {
  padding-left: 5px;
  font-size: 18px;
}
.tracking-steps article > p {
  font-size: 16px;
}
.order-status {
  padding-left: 5px;
  font-size: 18px;
  color: #ffa500;
  display: block;
}

@media screen and (max-width: 733px) {
  .tracking-steps article > p {
    line-height: 22px;
  }
  /* .delivery-day {
    font-size: 12px;
  } */
  .shipment-eta-container {
    flex-direction: column;
  }
  .delivery-day {
    padding-left: 0px;
    padding-top: 5px;
  }
}

@media screen and (min-width: 734px) {
  section {
    padding: 40px !important;
  }
  .shipment-header {
    line-height: 34px;
    font-size: 26px;
  }
  .tracking-steps li article {
    margin-left: 40px !important;
  }
  .shipment-state p + p {
    margin-top: 24px !important;
  }
}

.delay-banner {
  background-color: orange;
  color: white;
  padding: 10px;
  text-align: center;
  margin: 10px 0;
}

.reservation-section {
  display: flex;
  justify-content: flex-start;
  background: none;
  border: none;
}

.reservation-button {
  background-color: #ffa500;
  color: #fff;
  border: 2px solid #ffa500;
  width: auto;
  justify-self: flex-end;
}

.reservation-button:hover {
  background-color: #28a745;
  color: #fff;
  border-color: #28a745;
}

.phone-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.phone-modal {
  background-color: #fff;
  padding: 30px;
  border-radius: 8px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.phone-modal h2 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 20px;
  color: #333;
}

.phone-input-group {
  display: flex;
  margin-bottom: 20px;
}

.prefix-dropdown {
  width: 130px;
  height: 46px;
  padding: 0 10px;
  margin-right: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  font-size: 16px;
}

.phone-input {
  flex: 1;
  height: 46px;
  padding: 0 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
}

.error-message {
  color: #d9534f;
  margin-bottom: 15px;
  font-size: 14px;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.submit-btn {
  background-color: #4caf50 !important;
  border: none !important;
  color: #ffffff !important;
  font-weight: 500 !important;
  padding: 10px 20px !important;
  border-radius: 4px !important;
  cursor: pointer !important;
  width: auto !important;
  text-shadow: none !important;
}

.close-btn {
  background-color: #f44336 !important;
  border: none !important;
  color: #ffffff !important;
  font-weight: 500 !important;
  padding: 10px 20px !important;
  border-radius: 4px !important;
  cursor: pointer !important;
  width: auto !important;
  text-shadow: none !important;
}

.submit-btn:hover {
  background-color: #45a049 !important;
}

.close-btn:hover {
  background-color: #d32f2f !important;
}

.click-here {
  color: #ffa500;
  text-decoration: underline;
  cursor: pointer;
  font-weight: bold;
}

.click-here:hover {
  color: #e67700;
}

.tracking-link {
  color: #ffa500;
  text-decoration: underline;
  font-weight: bold;
}

.tracking-link:hover {
  color: #e67700;
  text-decoration: none;
}
.courier-name {
  font-weight: bold;
}
</style>
